import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, Target, Book, Download, Filter, BarChart2, Calendar, Zap, RefreshCw, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ReferenceLine } from 'recharts';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getMemorizationProgress, formatChartDate, getPhaseDistribution } from '../../services/analyticsService';
import { ChartSkeleton, StudyAnalyticsSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';
import MemoryHealthCard from '../dashboard/components/MemoryHealthCard';
import AwardsProgressPanel from './components/AwardsProgressPanel';

// Spaced repetition phase intervals in days
const PHASE_INTERVALS = { 0: 0, 1: 1, 2: 3, 3: 7, 4: 14, 5: 30, 6: 45, 7: 90 };

// All 66 Bible books organized by canonical groups
const BIBLE_BOOK_GROUPS = [
  {
    id: 'pentateuch',
    label: 'Pentateuch',
    testament: 'OT',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    borderColor: 'border-amber-200 dark:border-amber-800',
    books: ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy']
  },
  {
    id: 'historical',
    label: 'Historical Books',
    testament: 'OT',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    borderColor: 'border-orange-200 dark:border-orange-800',
    books: ['Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther']
  },
  {
    id: 'poetry',
    label: 'Poetry & Wisdom',
    testament: 'OT',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/30',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    books: ['Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Solomon']
  },
  {
    id: 'major-prophets',
    label: 'Major Prophets',
    testament: 'OT',
    color: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    borderColor: 'border-red-200 dark:border-red-800',
    books: ['Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel']
  },
  {
    id: 'minor-prophets',
    label: 'Minor Prophets',
    testament: 'OT',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50 dark:bg-rose-950/30',
    borderColor: 'border-rose-200 dark:border-rose-800',
    books: ['Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi']
  },
  {
    id: 'gospels',
    label: 'Gospels',
    testament: 'NT',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
    books: ['Matthew', 'Mark', 'Luke', 'John']
  },
  {
    id: 'acts',
    label: 'Acts',
    testament: 'NT',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/30',
    borderColor: 'border-cyan-200 dark:border-cyan-800',
    books: ['Acts']
  },
  {
    id: 'pauline',
    label: 'Pauline Epistles',
    testament: 'NT',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/30',
    borderColor: 'border-indigo-200 dark:border-indigo-800',
    books: ['Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy', '2 Timothy', 'Titus', 'Philemon']
  },
  {
    id: 'general-epistles',
    label: 'General Epistles',
    testament: 'NT',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50 dark:bg-violet-950/30',
    borderColor: 'border-violet-200 dark:border-violet-800',
    books: ['Hebrews', 'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude']
  },
  {
    id: 'revelation',
    label: 'Revelation',
    testament: 'NT',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    borderColor: 'border-purple-200 dark:border-purple-800',
    books: ['Revelation']
  }
];

const StudyAnalyticsDashboard = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('6months');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [totalVerses, setTotalVerses] = useState(0);
  const [totalPassages, setTotalPassages] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [progressChartData, setProgressChartData] = useState([]);
  const [progressLoading, setProgressLoading] = useState(true);
  const [chartTimeDomain, setChartTimeDomain] = useState('30days');
  const [phaseDistribution, setPhaseDistribution] = useState([]);
  const [phaseLoading, setPhaseLoading] = useState(true);
  const [calendarYear, setCalendarYear] = useState(new Date()?.getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [futureScheduleData, setFutureScheduleData] = useState([]);
  const [futureScheduleLoading, setFutureScheduleLoading] = useState(true);
  const [forecastData, setForecastData] = useState([]);
  const [forecastLoading, setForecastLoading] = useState(true);
  const [userSuccessRate, setUserSuccessRate] = useState(null);
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [reviewHistoryLoading, setReviewHistoryLoading] = useState(true);
  const [bookBreakdown, setBookBreakdown] = useState({});
  const [bookBreakdownLoading, setBookBreakdownLoading] = useState(true);
  const [dueToday, setDueToday] = useState(0);
  const [totalReviewsAllTime, setTotalReviewsAllTime] = useState(0);
  const [totalSuccessfulAllTime, setTotalSuccessfulAllTime] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [daysSinceReviewData, setDaysSinceReviewData] = useState([]);
  const [daysSinceReviewLoading, setDaysSinceReviewLoading] = useState(true);
  const [daysSinceAnimated, setDaysSinceAnimated] = useState(false);
  const daysSinceRef = useRef(null);
  const [cumulativeLineData, setCumulativeLineData] = useState([]);
  const [cumulativeAnimated, setCumulativeAnimated] = useState(false);
  const [cumulativeProgress, setCumulativeProgress] = useState(0);
  const cumulativeRef = useRef(null);
  const cumulativeRafRef = useRef(null);
  const [cumulativeScheduleData, setCumulativeScheduleData] = useState([]);
  const [cumulativeScheduleAnimated, setCumulativeScheduleAnimated] = useState(false);
  const [cumulativeScheduleProgress, setCumulativeScheduleProgress] = useState(0);
  const cumulativeScheduleRef = useRef(null);
  const cumulativeScheduleRafRef = useRef(null);
  const contentRef = useRef(null);

  const handleGeneratePDF = async () => {
    setIsPdfGenerating(true);
    try {
      const html2canvas = (await import('html2canvas'))?.default;
      const { jsPDF } = await import('jspdf');

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf?.internal?.pageSize?.getWidth();
      const pageHeight = pdf?.internal?.pageSize?.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;

      // Title page header
      pdf?.setFillColor(30, 41, 59);
      pdf?.rect(0, 0, pageWidth, 28, 'F');
      pdf?.setTextColor(255, 255, 255);
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Study Analytics Report', margin, 12);
      pdf?.setFontSize(9);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(`Generated: ${new Date()?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 20);
      pdf?.text(`User: ${user?.email || 'Unknown'}`, pageWidth - margin, 20, { align: 'right' });

      let yOffset = 36;

      // KPI summary section
      pdf?.setTextColor(30, 41, 59);
      pdf?.setFontSize(11);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Key Performance Indicators', margin, yOffset);
      yOffset += 6;

      const kpis = [
        { label: 'Total Verses', value: String(totalVerses) },
        { label: 'Total Passages', value: String(totalPassages) },
        { label: 'Current Streak', value: `${currentStreak} days` },
        { label: 'Longest Streak', value: `${longestStreak} days` },
        { label: 'Success Rate', value: userSuccessRate !== null ? `${Math.round(userSuccessRate * 100)}%` : 'N/A' },
        { label: 'Due Today', value: String(dueToday) },
        { label: 'Total Reviews', value: String(totalReviewsAllTime) },
        { label: 'Successful Reviews', value: String(totalSuccessfulAllTime) },
      ];

      const colW = contentWidth / 4;
      const rowH = 16;
      kpis?.forEach((kpi, i) => {
        const col = i % 4;
        let row = Math.floor(i / 4);
        const x = margin + col * colW;
        const y = yOffset + row * rowH;
        pdf?.setFillColor(248, 250, 252);
        pdf?.setDrawColor(226, 232, 240);
        pdf?.roundedRect(x, y, colW - 2, rowH - 2, 2, 2, 'FD');
        pdf?.setFontSize(13);
        pdf?.setFont('helvetica', 'bold');
        pdf?.setTextColor(30, 41, 59);
        pdf?.text(kpi?.value, x + (colW - 2) / 2, y + 6, { align: 'center' });
        pdf?.setFontSize(7);
        pdf?.setFont('helvetica', 'normal');
        pdf?.setTextColor(100, 116, 139);
        pdf?.text(kpi?.label, x + (colW - 2) / 2, y + 11, { align: 'center' });
      });

      yOffset += Math.ceil(kpis?.length / 4) * rowH + 8;

      // Phase distribution summary
      if (phaseDistribution?.length > 0) {
        pdf?.setFontSize(11);
        pdf?.setFont('helvetica', 'bold');
        pdf?.setTextColor(30, 41, 59);
        pdf?.text('Phase Distribution', margin, yOffset);
        yOffset += 5;
        const phaseColW = contentWidth / Math.min(phaseDistribution?.length, 8);
        phaseDistribution?.slice(0, 8)?.forEach((phase, i) => {
          const x = margin + i * phaseColW;
          pdf?.setFillColor(248, 250, 252);
          pdf?.setDrawColor(226, 232, 240);
          pdf?.roundedRect(x, yOffset, phaseColW - 2, 14, 2, 2, 'FD');
          pdf?.setFontSize(11);
          pdf?.setFont('helvetica', 'bold');
          pdf?.setTextColor(30, 41, 59);
          pdf?.text(String(phase?.value || phase?.count || 0), x + (phaseColW - 2) / 2, yOffset + 6, { align: 'center' });
          pdf?.setFontSize(7);
          pdf?.setFont('helvetica', 'normal');
          pdf?.setTextColor(100, 116, 139);
          pdf?.text(phase?.name || `Phase ${i}`, x + (phaseColW - 2) / 2, yOffset + 11, { align: 'center' });
        });
        yOffset += 20;
      }

      // Capture all chart sections from the DOM
      const chartSections = contentRef?.current?.querySelectorAll('[data-pdf-chart]');
      if (chartSections?.length > 0) {
        for (const section of chartSections) {
          const sectionTitle = section?.getAttribute('data-pdf-chart');
          try {
            const canvas = await html2canvas(section, {
              scale: 1.5,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false,
            });
            const imgData = canvas?.toDataURL('image/png');
            const imgAspect = canvas?.height / canvas?.width;
            const imgW = contentWidth;
            const imgH = imgW * imgAspect;

            // Check if we need a new page
            if (yOffset + imgH + 14 > pageHeight - margin) {
              pdf?.addPage();
              yOffset = margin;
            }

            // Section title
            pdf?.setFontSize(10);
            pdf?.setFont('helvetica', 'bold');
            pdf?.setTextColor(30, 41, 59);
            pdf?.text(sectionTitle, margin, yOffset);
            yOffset += 5;

            pdf?.addImage(imgData, 'PNG', margin, yOffset, imgW, imgH);
            yOffset += imgH + 10;
          } catch (err) {
            console.warn(`Failed to capture chart: ${sectionTitle}`, err);
          }
        }
      }

      // Footer on last page
      pdf?.setFontSize(8);
      pdf?.setFont('helvetica', 'normal');
      pdf?.setTextColor(148, 163, 184);
      pdf?.text('FortifiedMind.org — Scripture Memorization Analytics', pageWidth / 2, pageHeight - 6, { align: 'center' });

      const filename = `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      pdf?.save(filename);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev?.[groupId] }));
  };

  // Fetch basic KPIs
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!user?.id) { setLoading(false); return; }
      try {
        setLoading(true);
        const { data: versesData } = await supabase
          ?.from('verses')
          ?.select('verse_count, current_phase, next_review_date, review_count, successful_review_count')
          ?.eq('user_id', user?.id);

        const passages = versesData?.length || 0;
        const verses = versesData?.reduce((sum, v) => sum + (v?.verse_count || 1), 0) || 0;
        setTotalPassages(passages);
        setTotalVerses(verses);

        const todayStr = new Date()?.toISOString()?.split('T')?.[0];
        const dueCount = versesData?.filter(v =>
          v?.current_phase > 0 && v?.next_review_date && v?.next_review_date <= todayStr
        )?.length || 0;
        setDueToday(dueCount);

        const totalReviewed = versesData?.reduce((sum, v) => sum + (v?.review_count || 0), 0) || 0;
        const totalSuccessful = versesData?.reduce((sum, v) => sum + (v?.successful_review_count || 0), 0) || 0;
        setTotalReviewsAllTime(totalReviewed);
        setTotalSuccessfulAllTime(totalSuccessful);
        if (totalReviewed > 0) {
          const rawRate = totalSuccessful / totalReviewed;
          setUserSuccessRate(rawRate > 1 ? rawRate / 100 : rawRate);
        }

        const { data: streakData } = await supabase
          ?.from('user_streaks')
          ?.select('current_streak, longest_streak')
          ?.eq('user_id', user?.id)
          ?.single();
        setCurrentStreak(streakData?.current_streak || 0);
        setLongestStreak(streakData?.longest_streak || 0);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalyticsData();
  }, [user?.id]);

  // Fetch memorization progress chart data
  useEffect(() => {
    const fetchProgressData = async () => {
      if (!user?.id) { setProgressLoading(false); return; }
      try {
        setProgressLoading(true);
        const data = await getMemorizationProgress(user?.id, chartTimeDomain);
        setProgressChartData(data);
      } catch (error) {
        setProgressChartData([]);
      } finally {
        setProgressLoading(false);
      }
    };
    fetchProgressData();
  }, [user?.id, chartTimeDomain]);

  // Fetch phase distribution
  useEffect(() => {
    const fetchPhaseDistribution = async () => {
      if (!user?.id) { setPhaseLoading(false); return; }
      try {
        setPhaseLoading(true);
        const data = await getPhaseDistribution(user?.id);
        setPhaseDistribution(data);
      } catch (error) {
        setPhaseDistribution([]);
      } finally {
        setPhaseLoading(false);
      }
    };
    fetchPhaseDistribution();
  }, [user?.id]);

  // Fetch available years
  useEffect(() => {
    const fetchAvailableYears = async () => {
      if (!user?.id) { setAvailableYears([new Date()?.getFullYear()]); return; }
      try {
        const { data: earliestVerse } = await supabase
          ?.from('verses')
          ?.select('created_at')
          ?.eq('user_id', user?.id)
          ?.order('created_at', { ascending: true })
          ?.limit(1)
          ?.single();
        if (earliestVerse?.created_at) {
          const earliestYear = new Date(earliestVerse?.created_at)?.getFullYear();
          const currentYear = new Date()?.getFullYear();
          const years = [];
          for (let year = earliestYear; year <= currentYear; year++) years?.push(year);
          setAvailableYears(years);
        } else {
          setAvailableYears([new Date()?.getFullYear()]);
        }
      } catch (error) {
        setAvailableYears([new Date()?.getFullYear()]);
      }
    };
    fetchAvailableYears();
  }, [user?.id]);

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!user?.id) { setCalendarLoading(false); return; }
      try {
        setCalendarLoading(true);
        const startDate = `${calendarYear}-01-01`;
        const endDate = `${calendarYear}-12-31`;
        const { data: activities } = await supabase
          ?.from('user_daily_activity')
          ?.select('activity_date, qualifies_for_streak')
          ?.eq('user_id', user?.id)
          ?.gte('activity_date', startDate)
          ?.lte('activity_date', endDate);
        const dataMap = {};
        activities?.forEach(activity => { dataMap[activity?.activity_date] = activity?.qualifies_for_streak; });
        setCalendarData(dataMap);
      } catch (error) {
        setCalendarData({});
      } finally {
        setCalendarLoading(false);
      }
    };
    fetchCalendarData();
  }, [user?.id, calendarYear]);

  // Fetch future schedule data
  useEffect(() => {
    const fetchFutureScheduleData = async () => {
      if (!user?.id) { setFutureScheduleLoading(false); return; }
      try {
        setFutureScheduleLoading(true);
        const { data: verses } = await supabase
          ?.from('verses')
          ?.select('next_review_date')
          ?.eq('user_id', user?.id)
          ?.neq('current_phase', 0)
          ?.not('next_review_date', 'is', null);
        const dateMap = {};
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        verses?.forEach(verse => {
          const date = verse?.next_review_date;
          if (date) { dateMap[date] = (dateMap?.[date] || 0) + 1; }
        });
        const scheduleArray = Object.entries(dateMap)?.map(([date, count]) => {
          const reviewDate = new Date(date);
          reviewDate?.setHours(0, 0, 0, 0);
          return {
            date, count, isOverdue: reviewDate < today,
            displayDate: new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: reviewDate?.getFullYear() !== today?.getFullYear() ? 'numeric' : undefined })
          };
        })?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
        setFutureScheduleData(scheduleArray);
      } catch (error) {
        setFutureScheduleData([]);
      } finally {
        setFutureScheduleLoading(false);
      }
    };
    fetchFutureScheduleData();
  }, [user?.id]);

  // Fetch review history
  useEffect(() => {
    const fetchReviewHistory = async () => {
      if (!user?.id) { setReviewHistoryLoading(false); return; }
      try {
        setReviewHistoryLoading(true);
        const { data: activities } = await supabase
          ?.from('user_daily_activity')
          ?.select('activity_date, reviews_completed, verses_practiced, memorization_sessions')
          ?.eq('user_id', user?.id)
          ?.order('activity_date', { ascending: true })
          ?.limit(60);

        if (activities?.length > 0) {
          const reviewData = activities?.map(a => ({
            date: new Date(a?.activity_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            reviews: a?.reviews_completed || 0,
            practiced: a?.verses_practiced || 0,
            sessions: a?.memorization_sessions || 0
          }));
          setReviewHistoryData(reviewData);
        }
      } catch (error) {
        console.error('Error fetching review history:', error);
      } finally {
        setReviewHistoryLoading(false);
      }
    };
    fetchReviewHistory();
  }, [user?.id]);

  // Fetch book breakdown — all books, grouped by canonical section
  useEffect(() => {
    const fetchBookBreakdown = async () => {
      if (!user?.id) { setBookBreakdownLoading(false); return; }
      try {
        setBookBreakdownLoading(true);
        const { data: verses } = await supabase
          ?.from('verses')
          ?.select('book, current_phase, review_count, successful_review_count, verse_count')
          ?.eq('user_id', user?.id);

        // Build a map of book → stats from user data
        const bookMap = {};
        verses?.forEach(v => {
          const book = v?.book || 'Unknown';
          if (!bookMap?.[book]) bookMap[book] = { passages: 0, verses: 0, totalReviewed: 0, totalSuccessful: 0 };
          bookMap[book].passages++;
          bookMap[book].verses += v?.verse_count || 1;
          bookMap[book].totalReviewed += v?.review_count || 0;
          bookMap[book].totalSuccessful += v?.successful_review_count || 0;
        });

        // Merge into group structure — include ALL books, even those with 0 verses
        const grouped = {};
        BIBLE_BOOK_GROUPS?.forEach(group => {
          const booksData = group?.books?.map(bookName => {
            const stats = bookMap?.[bookName] || { passages: 0, verses: 0, totalReviewed: 0, totalSuccessful: 0 };
            return {
              book: bookName,
              ...stats,
              accuracy: stats?.totalReviewed > 0 ? (() => {
                const raw = stats?.totalSuccessful / stats?.totalReviewed;
                return Math.round((raw > 1 ? raw / 100 : raw) * 100);
              })() : null
            };
          });

          const groupTotals = booksData?.reduce((acc, b) => ({
            passages: acc?.passages + b?.passages,
            verses: acc?.verses + b?.verses,
            totalReviewed: acc?.totalReviewed + b?.totalReviewed,
            totalSuccessful: acc?.totalSuccessful + b?.totalSuccessful
          }), { passages: 0, verses: 0, totalReviewed: 0, totalSuccessful: 0 });

          grouped[group.id] = {
            ...group,
            books: booksData,
            totals: {
              ...groupTotals,
              accuracy: groupTotals?.totalReviewed > 0 ? (() => {
                const raw = groupTotals?.totalSuccessful / groupTotals?.totalReviewed;
                return Math.round((raw > 1 ? raw / 100 : raw) * 100);
              })() : null
            }
          };
        });

        setBookBreakdown(grouped);
      } catch (error) {
        console.error('Error fetching book breakdown:', error);
      } finally {
        setBookBreakdownLoading(false);
      }
    };
    fetchBookBreakdown();
  }, [user?.id]);

  // Build 90-day forecast histogram
  useEffect(() => {
    const buildForecast = async () => {
      if (!user?.id) { setForecastLoading(false); return; }
      try {
        setForecastLoading(true);
        const { data: verses } = await supabase
          ?.from('verses')
          ?.select('current_phase, next_review_date, review_count, successful_review_count, verse_count')
          ?.eq('user_id', user?.id)
          ?.neq('current_phase', 0);

        if (!verses || verses?.length === 0) { setForecastLoading(false); return; }

        const totalReviewed = verses?.reduce((sum, v) => sum + (v?.review_count || 0), 0);
        const totalSuccessful = verses?.reduce((sum, v) => sum + (v?.successful_review_count || 0), 0);
        const rawSuccessRate = totalReviewed >= 10 ? totalSuccessful / totalReviewed : null;
        const successRate = rawSuccessRate !== null ? (rawSuccessRate > 1 ? rawSuccessRate / 100 : rawSuccessRate) : 0.9;

        const today = new Date();
        today?.setHours(0, 0, 0, 0);

        const dailyCounts = {};
        for (let i = 0; i < 90; i++) {
          const d = new Date(today);
          d?.setDate(d?.getDate() + i);
          dailyCounts[d?.toISOString()?.split('T')?.[0]] = 0;
        }

        verses?.forEach((verse) => {
          let nextReview = verse?.next_review_date ? new Date(verse?.next_review_date) : new Date(today);
          nextReview?.setHours(0, 0, 0, 0);
          let phase = verse?.current_phase || 1;
          const verseWeight = verse?.verse_count || 1;

          for (let attempt = 0; attempt < 30; attempt++) {
            const dateStr = nextReview?.toISOString()?.split('T')?.[0];
            const diffDays = Math.floor((nextReview - today) / (1000 * 60 * 60 * 24));

            if (diffDays >= 90) break;

            if (dailyCounts?.hasOwnProperty(dateStr)) {
              dailyCounts[dateStr] += verseWeight;
            }

            const succeeds = successRate >= 0.5;
            if (succeeds && phase < 7) {
              phase = Math.min(phase + 1, 7);
            } else if (!succeeds && phase > 1) {
              phase = Math.max(phase - 1, 1);
            }

            const interval = PHASE_INTERVALS?.[phase] || 1;
            const safeInterval = Math.max(interval, 1);
            nextReview = new Date(nextReview);
            nextReview?.setDate(nextReview?.getDate() + safeInterval);
          }
        });

        const forecastArray = Object.entries(dailyCounts)?.map(([date, count]) => {
          const d = new Date(date);
          const dayNum = Math.floor((d - today) / (1000 * 60 * 60 * 24));
          return {
            day: dayNum, date, count,
            displayDate: d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            week: `Week ${Math.floor(dayNum / 7) + 1}`
          };
        })?.sort((a, b) => a?.day - b?.day);

        setForecastData(forecastArray);
        if (userSuccessRate === null && totalReviewed >= 10) {
          setUserSuccessRate(successRate);
        } else if (userSuccessRate === null) {
          setUserSuccessRate(0.9);
        }
      } catch (error) {
        console.error('Error building forecast:', error);
      } finally {
        setForecastLoading(false);
      }
    };
    buildForecast();
  }, [user?.id]);

  // Fetch days since last review histogram data
  useEffect(() => {
    const fetchDaysSinceReview = async () => {
      if (!user?.id) { setDaysSinceReviewLoading(false); return; }
      try {
        setDaysSinceReviewLoading(true);
        const { data: verses } = await supabase
          ?.from('verses')
          ?.select('last_reviewed_at, verse_count, current_phase')
          ?.eq('user_id', user?.id)
          ?.neq('current_phase', 0)
          ?.not('last_reviewed_at', 'is', null);

        if (!verses || verses?.length === 0) {
          setDaysSinceReviewData([]);
          setDaysSinceReviewLoading(false);
          return;
        }

        const today = new Date();
        today?.setHours(0, 0, 0, 0);

        // Count verses per "days since last review" bucket
        const bucketMap = {};
        verses?.forEach(verse => {
          const lastReviewed = new Date(verse?.last_reviewed_at);
          lastReviewed?.setHours(0, 0, 0, 0);
          const diffMs = today - lastReviewed;
          const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
          const verseWeight = verse?.verse_count || 1;
          bucketMap[days] = (bucketMap?.[days] || 0) + verseWeight;
        });

        // Build sorted array of { days, count, color }
        const getColor = (days) => {
          if (days <= 30) return '#22c55e';   // green
          if (days <= 45) return '#eab308';   // yellow
          if (days <= 60) return '#f97316';   // orange
          return '#ef4444';                    // red
        };

        const histogramData = Object.entries(bucketMap)
          ?.map(([days, count]) => ({
            days: Number(days),
            count,
            color: getColor(Number(days)),
            label: `${days}d`
          }))
          ?.sort((a, b) => a?.days - b?.days);

        setDaysSinceReviewData(histogramData);
      } catch (error) {
        console.error('Error fetching days since review data:', error);
        setDaysSinceReviewData([]);
      } finally {
        setDaysSinceReviewLoading(false);
      }
    };
    fetchDaysSinceReview();
  }, [user?.id]);

  // Intersection observer to trigger animation when chart scrolls into view
  useEffect(() => {
    if (!daysSinceRef?.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry?.isIntersecting) setDaysSinceAnimated(true); },
      { threshold: 0.3 }
    );
    observer?.observe(daysSinceRef?.current);
    return () => observer?.disconnect();
  }, [daysSinceRef?.current, daysSinceReviewLoading]);

  // Build cumulative days-since-last-review line data (0–90 days)
  useEffect(() => {
    if (daysSinceReviewData?.length === 0) { setCumulativeLineData([]); return; }
    // Build a map of day -> count from histogram data
    const dayMap = {};
    daysSinceReviewData?.forEach(d => { dayMap[d?.days] = d?.count; });
    // Build cumulative array: for each day 0-90, sum all verses with days <= that day
    const result = [];
    let running = 0;
    for (let day = 0; day <= 90; day++) {
      running += dayMap?.[day] || 0;
      result?.push({ day, cumulative: running });
    }
    setCumulativeLineData(result);
  }, [daysSinceReviewData]);

  // Intersection observer for cumulative line chart animation
  useEffect(() => {
    if (!cumulativeRef?.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry?.isIntersecting) setCumulativeAnimated(true); },
      { threshold: 0.3 }
    );
    observer?.observe(cumulativeRef?.current);
    return () => observer?.disconnect();
  }, [cumulativeRef?.current, daysSinceReviewLoading]);

  // Animate the cumulative line chart progress (0 → 1)
  useEffect(() => {
    if (!cumulativeAnimated) return;
    const duration = 1200;
    let start = null;
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease in-out cubic
      const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      setCumulativeProgress(eased);
      if (progress < 1) cumulativeRafRef.current = requestAnimationFrame(animate);
    };
    cumulativeRafRef.current = requestAnimationFrame(animate);
    return () => { if (cumulativeRafRef?.current) cancelAnimationFrame(cumulativeRafRef?.current); };
  }, [cumulativeAnimated]);

  // Fetch cumulative scheduled verses line data (today + 90 days out)
  useEffect(() => {
    if (!futureScheduleData || futureScheduleData?.length === 0) { setCumulativeScheduleData([]); return; }
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    // Build a map of dateStr -> count from futureScheduleData
    const dateMap = {};
    futureScheduleData?.forEach(d => { if (d?.date) dateMap[d.date] = (dateMap?.[d?.date] || 0) + (d?.count || 0); });
    // Build cumulative array for each day 0-90
    const result = [];
    let running = 0;
    for (let i = 0; i <= 90; i++) {
      const d = new Date(today);
      d?.setDate(d?.getDate() + i);
      const dateStr = d?.toISOString()?.split('T')?.[0];
      running += dateMap?.[dateStr] || 0;
      result?.push({
        date: dateStr,
        displayDate: d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cumulative: running,
        day: i
      });
    }
    setCumulativeScheduleData(result);
  }, [futureScheduleData]);

  // Intersection observer for cumulative schedule line chart animation
  useEffect(() => {
    if (!cumulativeScheduleRef?.current) return;
    if (cumulativeScheduleData?.length === 0) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !cumulativeScheduleAnimated) setCumulativeScheduleAnimated(true); },
      { threshold: 0.3 }
    );
    observer?.observe(cumulativeScheduleRef?.current);
    return () => observer?.disconnect();
  }, [cumulativeScheduleRef?.current, cumulativeScheduleData?.length]);

  useEffect(() => {
    if (!cumulativeScheduleAnimated) return;
    const duration = 1800;
    let start = performance.now();
    const animate = (timestamp) => {
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      setCumulativeScheduleProgress(eased);
      if (progress < 1) cumulativeScheduleRafRef.current = requestAnimationFrame(animate);
    };
    cumulativeScheduleRafRef.current = requestAnimationFrame(animate);
    return () => { if (cumulativeScheduleRafRef?.current) cancelAnimationFrame(cumulativeScheduleRafRef?.current); };
  }, [cumulativeScheduleAnimated]);

  // Custom animated bar shape for days-since-review histogram
  const AnimatedBar = (props) => {
    const { x, y, width, height, payload } = props;
    const [animHeight, setAnimHeight] = useState(0);
    const [animY, setAnimY] = useState(y + height);
    const rafRef = useRef(null);
    const startRef = useRef(null);
    const duration = 600;

    useEffect(() => {
      if (!daysSinceAnimated) return;
      startRef.current = null;
      const animate = (timestamp) => {
        if (!startRef?.current) startRef.current = timestamp;
        const elapsed = timestamp - startRef?.current;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setAnimHeight(height * eased);
        setAnimY(y + height - height * eased);
        if (progress < 1) rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
      return () => { if (rafRef?.current) cancelAnimationFrame(rafRef?.current); };
    }, [daysSinceAnimated, height, y]);

    return (
      <rect
        x={x}
        y={animY}
        width={width}
        height={animHeight}
        fill={payload?.color}
        rx={2}
        ry={2}
      />
    );
  };

  const CalendarHeatmap = () => {
    const generateCalendarGrid = () => {
      const grid = Array.from({ length: 7 }, () => []);
      const startDate = new Date(calendarYear, 0, 1);
      const endDate = new Date(calendarYear, 11, 31);
      const firstDayOfWeek = startDate?.getDay();
      for (let row = 0; row < firstDayOfWeek; row++) grid?.[row]?.push(null);
      let currentRow = firstDayOfWeek;
      for (let d = new Date(startDate); d <= endDate; d?.setDate(d?.getDate() + 1)) {
        const dateStr = d?.toISOString()?.split('T')?.[0];
        grid?.[currentRow]?.push({ date: new Date(d), completed: calendarData?.[dateStr], dateStr });
        currentRow++;
        if (currentRow === 7) currentRow = 0;
      }
      const maxLength = Math.max(...grid?.map(row => row?.length));
      grid?.forEach(row => { while (row?.length < maxLength) row?.push(null); });
      return grid;
    };

    const grid = generateCalendarGrid();
    const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">{calendarYear} Review Calendar</h3>
          {availableYears?.length > 1 && (
            <select value={calendarYear} onChange={(e) => setCalendarYear(Number(e?.target?.value))}
              className="px-3 py-1 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent">
              {availableYears?.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          )}
        </div>
        {calendarLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="flex gap-2">
                <div className="flex flex-col gap-1 pt-1">
                  {dayLabels?.map((label, index) => (
                    <div key={index} className="w-4 h-3 flex items-center justify-center text-xs text-muted-foreground font-medium">{label}</div>
                  ))}
                </div>
                <div className="flex gap-1">
                  {grid?.[0]?.map((_, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-1">
                      {grid?.map((row, dayIndex) => {
                        let day = row?.[weekIndex];
                        return (
                          <div key={dayIndex}
                            className={`w-3 h-3 rounded-sm ${day === null ? 'bg-transparent' : day?.completed ? 'bg-success' : 'bg-muted'}`}
                            title={day ? `${day?.date?.toDateString()}: ${day?.completed ? 'Completed' : 'Missed'}` : ''}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
              <span>Less</span>
              <div className="flex gap-1">
                <div className="w-3 h-3 bg-muted rounded-sm"></div>
                <div className="w-3 h-3 bg-success/30 rounded-sm"></div>
                <div className="w-3 h-3 bg-success/60 rounded-sm"></div>
                <div className="w-3 h-3 bg-success rounded-sm"></div>
              </div>
              <span>More</span>
            </div>
          </>
        )}
      </div>
    );
  };

  // Accuracy badge helper
  const AccuracyBadge = ({ accuracy, reviewed }) => {
    if (reviewed === 0 || accuracy === null) return <span className="text-xs text-muted-foreground">—</span>;
    const color = accuracy >= 80 ? 'text-success' : accuracy >= 60 ? 'text-warning' : 'text-destructive';
    return <span className={`text-sm font-semibold ${color}`}>{accuracy}%</span>;
  };

  // Mini progress bar
  const MiniBar = ({ accuracy }) => (
    <div className="w-full bg-muted rounded-full h-1.5">
      <div
        className={`h-1.5 rounded-full transition-all ${accuracy >= 80 ? 'bg-success' : accuracy >= 60 ? 'bg-warning' : accuracy > 0 ? 'bg-destructive' : 'bg-muted'}`}
        style={{ width: `${accuracy || 0}%` }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header title="Study Analytics Dashboard" />
      {loading ? (
        <div className="max-w-7xl mx-auto p-6">
          <StudyAnalyticsSkeleton />
        </div>
      ) : (
      <div className="max-w-7xl mx-auto p-6" ref={contentRef}>
        <FadeSlideIn>
        {/* Header Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <select value={timeRange} onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent">
              <option value="1month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
              <option value="all">All Time</option>
            </select>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent">
              <option value="all">All Categories</option>
              <option value="psalms">Psalms</option>
              <option value="proverbs">Proverbs</option>
              <option value="gospels">Gospels</option>
              <option value="epistles">Epistles</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleGeneratePDF}
              disabled={isPdfGenerating}
            >
              {isPdfGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Generate PDF
                </>
              )}
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <Download className="w-4 h-4" />Export Data
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <Filter className="w-4 h-4" />Advanced Filters
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-card rounded-lg border border-border mb-6">
          <div className="border-b border-border">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {['overview', 'progress', 'performance', 'forecast', 'awards']?.map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors whitespace-nowrap ${
                    activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}>
                  {tab === 'awards' ? '🏆 Awards' : tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              {/* Total Verses */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Verses</p>
                    {loading ? <div className="h-9 w-16 bg-muted animate-pulse rounded mt-1"></div> : (
                      <p className="text-3xl font-bold text-foreground">{totalVerses}</p>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Book className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  {loading ? <div className="h-4 w-24 bg-muted animate-pulse rounded"></div> : (
                    <span>{totalPassages} passage{totalPassages !== 1 ? 's' : ''} in library</span>
                  )}
                </div>
              </div>

              {/* Current Streak */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Current Streak</p>
                    {loading ? <div className="h-9 w-16 bg-muted animate-pulse rounded mt-1"></div> : (
                      <p className="text-3xl font-bold text-foreground">{currentStreak} <span className="text-lg font-normal text-muted-foreground">days</span></p>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-warning" />
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  {loading ? <div className="h-4 w-24 bg-muted animate-pulse rounded"></div> : (
                    <span>Best: {longestStreak} day{longestStreak !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>

              {/* Success Rate */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                    {loading ? <div className="h-9 w-16 bg-muted animate-pulse rounded mt-1"></div> : (
                      <p className="text-3xl font-bold text-foreground">
                        {userSuccessRate !== null ? `${Math.round(userSuccessRate * 100)}%` : '—'}
                      </p>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-success" />
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  {loading ? <div className="h-4 w-24 bg-muted animate-pulse rounded"></div> : (
                    <span>{totalReviewsAllTime} total review{totalReviewsAllTime !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>

              {/* Due Today */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Due Today</p>
                    {loading ? <div className="h-9 w-16 bg-muted animate-pulse rounded mt-1"></div> : (
                      <p className="text-3xl font-bold text-foreground">{dueToday}</p>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  {loading ? <div className="h-4 w-24 bg-muted animate-pulse rounded"></div> : (
                    <span>Verses due for review</span>
                  )}
                </div>
              </div>
            </div>

            {/* Memory Health Card */}
            <div className="mb-8">
              <MemoryHealthCard onExtraReview={null} />
            </div>

            {/* Days Since Last Review Histogram */}
            <div ref={daysSinceRef} data-pdf-chart="Days Since Last Review" className="bg-card rounded-lg border border-border p-6 mb-8">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-foreground">Days Since Last Review</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Number of verses by how long ago they were last reviewed</p>
              {daysSinceReviewLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : daysSinceReviewData?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={daysSinceReviewData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis
                        dataKey="label"
                        label={{ value: 'Days Since Last Review', position: 'insideBottom', offset: -45, fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                        tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval={Math.max(0, Math.floor(daysSinceReviewData?.length / 20) - 1)}
                      />
                      <YAxis
                        label={{ value: 'Verses', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                        tick={{ fill: 'var(--color-muted-foreground)' }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload?.length) {
                            const d = payload?.[0]?.payload;
                            const bracket =
                              d?.days <= 30 ? '≤ 30 days (On Track)' :
                              d?.days <= 45 ? '31–45 days (Attention)' :
                              d?.days <= 60 ? '46–60 days (Overdue)': '61+ days (Critical)';
                            return (
                              <div className="bg-card border border-border rounded-lg shadow-lg p-3">
                                <p className="font-semibold text-foreground">{d?.days} day{d?.days !== 1 ? 's' : ''} ago</p>
                                <p className="text-sm text-muted-foreground">{d?.count} verse{d?.count !== 1 ? 's' : ''}</p>
                                <p className="text-xs mt-1 font-medium" style={{ color: d?.color }}>{bracket}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="count" shape={<AnimatedBar />} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{ backgroundColor: '#22c55e' }}></div><span className="text-muted-foreground">≤ 30 days (On Track)</span></div>
                    <div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{ backgroundColor: '#eab308' }}></div><span className="text-muted-foreground">31–45 days (Attention)</span></div>
                    <div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{ backgroundColor: '#f97316' }}></div><span className="text-muted-foreground">46–60 days (Overdue)</span></div>
                    <div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div><span className="text-muted-foreground">61+ days (Critical)</span></div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">No review history available yet</div>
              )}
            </div>

            {/* Cumulative Days Since Last Review Line Chart */}
            <div ref={cumulativeRef} data-pdf-chart="Cumulative Days Since Last Review" className="bg-card rounded-lg border border-border p-6 mb-8">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-foreground">Cumulative Days Since Last Review</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">How many verses have been reviewed within X days (cumulative, 0–90 day window)</p>
              {daysSinceReviewLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : cumulativeLineData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={cumulativeLineData?.slice(0, Math.round(cumulativeProgress * 91))}
                    margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                  >
                    <defs>
                      <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="33%" stopColor="#eab308" />
                        <stop offset="55%" stopColor="#f97316" />
                        <stop offset="100%" stopColor="#ef4444" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="day"
                      type="number"
                      domain={[0, 90]}
                      ticks={[0, 10, 20, 30, 40, 45, 50, 60, 70, 80, 90]}
                      label={{ value: 'Days Since Last Review', position: 'insideBottom', offset: -30, fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                      tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                      height={55}
                    />
                    <YAxis
                      label={{ value: 'Verses (Cumulative)', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)', fontSize: 12, dx: -5 }}
                      tick={{ fill: 'var(--color-muted-foreground)' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload?.length) {
                          const d = payload?.[0]?.payload;
                          const bracket =
                            d?.day <= 30 ? { label: 'On Track', color: '#22c55e' } :
                            d?.day <= 45 ? { label: 'Attention', color: '#eab308' } :
                            d?.day <= 60 ? { label: 'Overdue', color: '#f97316' } :
                            { label: 'Critical', color: '#ef4444' };
                          return (
                            <div className="bg-card border border-border rounded-lg shadow-lg p-3">
                              <p className="font-semibold text-foreground">Day {d?.day}</p>
                              <p className="text-sm text-muted-foreground">{d?.cumulative} verse{d?.cumulative !== 1 ? 's' : ''} reviewed within {d?.day} day{d?.day !== 1 ? 's' : ''}</p>
                              <p className="text-xs mt-1 font-medium" style={{ color: bracket?.color }}>{bracket?.label}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <ReferenceLine y={Math.round(cumulativeLineData?.reduce((sum, d) => sum + d?.cumulative, 0) / cumulativeLineData?.length)}
                      stroke="var(--color-warning)" strokeDasharray="4 4"
                      label={{ value: 'Avg', fill: 'var(--color-warning)', fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="cumulative"
                      stroke="url(#cumulativeGradient)"
                      strokeWidth={2.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">No review history available yet</div>
              )}
              {cumulativeLineData?.length > 0 && (
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2"><div className="w-4 h-1 rounded" style={{ backgroundColor: '#22c55e' }}></div><span className="text-muted-foreground">≤ 30 days (On Track)</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-1 rounded" style={{ backgroundColor: '#eab308' }}></div><span className="text-muted-foreground">31–45 days (Attention)</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-1 rounded" style={{ backgroundColor: '#f97316' }}></div><span className="text-muted-foreground">46–60 days (Overdue)</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-1 rounded" style={{ backgroundColor: '#ef4444' }}></div><span className="text-muted-foreground">61+ days (Critical)</span></div>
                </div>
              )}
            </div>

            {/* Cumulative Scheduled Verses Line Chart */}
            <div ref={cumulativeScheduleRef} data-pdf-chart="Cumulative Verses Scheduled (90 Days)" className="bg-card rounded-lg border border-border p-6 mb-8">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-foreground">Cumulative Verses Scheduled</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Total cumulative verses scheduled over the next 90 days (Verse Universe)</p>
              {futureScheduleLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : cumulativeScheduleData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={cumulativeScheduleData?.slice(0, Math.max(1, Math.round(cumulativeScheduleProgress * 91)))}
                    margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                  >
                    <defs>
                      <linearGradient id="cumSchedGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="33%" stopColor="#6366f1" />
                        <stop offset="66%" stopColor="#8b5cf6" />
                        <stop offset="100%" stopColor="#a855f7" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="displayDate"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={Math.floor(cumulativeScheduleData?.length / 10) || 8}
                      tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                      label={{ value: 'Date', position: 'insideBottom', offset: -30, fill: 'var(--color-muted-foreground)' }}
                    />
                    <YAxis
                      tick={{ fill: 'var(--color-muted-foreground)' }}
                      label={{ value: 'Cumulative Verses', angle: -90, position: 'insideLeft', offset: 10, fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }}
                      formatter={(value) => [value, 'Cumulative Verses']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <ReferenceLine x={cumulativeScheduleData?.[29]?.displayDate} stroke="#22c55e" strokeDasharray="4 4" label={{ value: '30d', fill: '#22c55e', fontSize: 10 }} />
                    <ReferenceLine x={cumulativeScheduleData?.[44]?.displayDate} stroke="#eab308" strokeDasharray="4 4" label={{ value: '45d', fill: '#eab308', fontSize: 10 }} />
                    <ReferenceLine x={cumulativeScheduleData?.[59]?.displayDate} stroke="#f97316" strokeDasharray="4 4" label={{ value: '60d', fill: '#f97316', fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="cumulative"
                      stroke="url(#cumSchedGradient)"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 5, fill: '#6366f1' }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">No scheduled verses data available yet</div>
              )}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
              {/* Progress Over Time */}
              <div data-pdf-chart="Memorization Progress" className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Memorization Progress</h3>
                  <select value={chartTimeDomain} onChange={(e) => setChartTimeDomain(e?.target?.value)}
                    className="px-3 py-1 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent">
                    <option value="30days">30 Days</option>
                    <option value="90days">90 Days</option>
                    <option value="year">Year</option>
                    <option value="alltime">All Time</option>
                  </select>
                </div>
                {progressLoading ? (
                  <ChartSkeleton height="h-[300px]" />
                ) : progressChartData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={progressChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="date" tickFormatter={(date) => formatChartDate(date, chartTimeDomain)} angle={-45} textAnchor="end" height={80} tick={{ fill: 'var(--color-muted-foreground)' }} />
                      <YAxis tick={{ fill: 'var(--color-muted-foreground)' }} />
                      <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }}
                        labelFormatter={(date) => formatChartDate(date, chartTimeDomain)}
                        formatter={(value, name) => {
                          const labels = { phase1_2: 'Phase 1+2', phase3_4: 'Phase 3+4', phase5_6: 'Phase 5+6', phase7: 'Phase 7', total: 'Total Verses' };
                          return [value, labels?.[name] || name];
                        }} />
                      <Line type="monotone" dataKey="phase1_2" stroke="#ef4444" strokeWidth={2} />
                      <Line type="monotone" dataKey="phase3_4" stroke="var(--color-warning)" strokeWidth={2} />
                      <Line type="monotone" dataKey="phase5_6" stroke="var(--color-primary)" strokeWidth={2} />
                      <Line type="monotone" dataKey="phase7" stroke="var(--color-success)" strokeWidth={2} />
                      <Line type="monotone" dataKey="total" stroke="var(--color-accent)" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">No memorization data available yet</div>
                )}
              </div>

              {/* Phase Distribution */}
              <div data-pdf-chart="Phase Distribution" className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Phase Distribution</h3>
                {phaseLoading ? (
                  <ChartSkeleton height="h-[300px]" />
                ) : phaseDistribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={phaseDistribution} cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {phaseDistribution?.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">No phase distribution data available yet</div>
                )}
              </div>
            </div>

            {/* Future Schedule Histogram */}
            <div data-pdf-chart="Scheduled Reviews" className="bg-card rounded-lg border border-border p-6 mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-1">Scheduled Reviews</h3>
              <p className="text-sm text-muted-foreground mb-4">Actual review dates from your verse library (based on current next_review_date)</p>
              {futureScheduleLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : futureScheduleData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={futureScheduleData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="displayDate" angle={-45} textAnchor="end" height={80} interval={Math.floor(futureScheduleData?.length / 20) || 0} tick={{ fill: 'var(--color-muted-foreground)' }} />
                    <YAxis label={{ value: 'Verses', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)' }} tick={{ fill: 'var(--color-muted-foreground)' }} />
                    <Tooltip content={({ active, payload }) => {
                      if (active && payload && payload?.length) {
                        const data = payload?.[0]?.payload;
                        return (
                          <div className="bg-card border border-border rounded-lg shadow-lg p-3">
                            <p className="font-semibold text-foreground">{data?.displayDate}</p>
                            <p className="text-sm text-muted-foreground">{data?.count} verse{data?.count !== 1 ? 's' : ''} to review</p>
                            {data?.isOverdue && <p className="text-sm text-destructive font-medium mt-1">Overdue</p>}
                          </div>
                        );
                      }
                      return null;
                    }} />
                    <Bar dataKey="count" fill="var(--color-primary)"
                      shape={(props) => {
                        const { x, y, width, height, payload } = props;
                        return <rect x={x} y={y} width={width} height={height} fill={payload?.isOverdue ? 'var(--color-destructive)' : 'var(--color-primary)'} />;
                      }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">No scheduled reviews available yet</div>
              )}
              <div className="flex items-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-primary rounded"></div><span className="text-muted-foreground">Upcoming</span></div>
                <div className="flex items-center gap-2"><div className="w-4 h-4 bg-destructive rounded"></div><span className="text-muted-foreground">Overdue</span></div>
              </div>
            </div>

            {/* Calendar Heatmap */}
            <CalendarHeatmap />
          </>
        )}

        {activeTab === 'progress' && (
          <>
            {/* Daily Review Activity */}
            <div data-pdf-chart="Daily Review Activity" className="bg-card rounded-lg border border-border p-6 mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Daily Review Activity</h3>
              <p className="text-sm text-muted-foreground mb-4">Number of reviews completed per day</p>
              {reviewHistoryLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : reviewHistoryData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reviewHistoryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(reviewHistoryData?.length / 10) || 0} tick={{ fill: 'var(--color-muted-foreground)' }} />
                    <YAxis tick={{ fill: 'var(--color-muted-foreground)' }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }}
                      formatter={(value, name) => {
                        const labels = { reviews: 'Reviews Completed', practiced: 'Verses Practiced', sessions: 'Sessions' };
                        return [value, labels?.[name] || name];
                      }} />
                    <Bar dataKey="reviews" fill="var(--color-primary)" radius={[2, 2, 0, 0]} name="reviews" />
                    <Bar dataKey="practiced" fill="var(--color-success)" radius={[2, 2, 0, 0]} name="practiced" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">No review activity data available yet</div>
              )}
              {reviewHistoryData?.length > 0 && (
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2"><div className="w-4 h-4 bg-primary rounded"></div><span className="text-muted-foreground">Reviews Completed</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-4 bg-success rounded"></div><span className="text-muted-foreground">Verses Practiced</span></div>
                </div>
              )}
            </div>

            {/* Phase Distribution Bar */}
            <div data-pdf-chart="Verses by Phase" className="bg-card rounded-lg border border-border p-6 mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Verses by Phase</h3>
              <p className="text-sm text-muted-foreground mb-4">Distribution of your verses across spaced repetition phases</p>
              {phaseLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : phaseDistribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={phaseDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis type="number" tick={{ fill: 'var(--color-muted-foreground)' }} />
                    <YAxis dataKey="name" type="category" width={130} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {phaseDistribution?.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-muted-foreground">No phase data available yet</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'performance' && (
          <>
            {/* Performance by Bible Book — Grouped */}
            <div data-pdf-chart="Performance by Bible Book" className="bg-card rounded-lg border border-border p-6 mb-8">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-foreground">Performance by Bible Book</h3>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success inline-block"></span>≥80% accuracy</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning inline-block"></span>60–79%</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive inline-block"></span>&lt;60%</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">All 66 books grouped by canonical section. Click a group to expand individual books.</p>

              {bookBreakdownLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {BIBLE_BOOK_GROUPS?.map(groupDef => {
                    const group = bookBreakdown?.[groupDef?.id];
                    if (!group) return null;
                    const isExpanded = expandedGroups?.[groupDef?.id];
                    const hasData = group?.totals?.verses > 0;
                    const booksWithData = group?.books?.filter(b => b?.verses > 0)?.length;

                    return (
                      <div key={groupDef?.id} className={`rounded-lg border overflow-hidden ${group?.borderColor}`}>
                        {/* Group Header Row */}
                        <button
                          onClick={() => toggleGroup(groupDef?.id)}
                          className={`w-full flex items-center justify-between px-4 py-3 bg-muted/30 dark:bg-muted/20 hover:bg-muted/50 dark:hover:bg-muted/30 transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={isExpanded ? '' : ''}>
                              {isExpanded
                                ? <ChevronDown className={`w-4 h-4 ${group?.color}`} />
                                : <ChevronRight className={`w-4 h-4 ${group?.color}`} />
                              }
                            </span>
                            <div className="text-left">
                              <span className={`font-semibold text-sm ${group?.color}`}>{group?.label}</span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                {group?.books?.length} book{group?.books?.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${group?.testament === 'OT' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'}`}>
                              {group?.testament}
                            </span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-muted-foreground">Verses</p>
                              <p className="font-semibold text-foreground">{group?.totals?.verses || '—'}</p>
                            </div>
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-muted-foreground">Reviews</p>
                              <p className="font-semibold text-foreground">{group?.totals?.totalReviewed || '—'}</p>
                            </div>
                            <div className="text-right min-w-[60px]">
                              <p className="text-xs text-muted-foreground">Accuracy</p>
                              <AccuracyBadge accuracy={group?.totals?.accuracy} reviewed={group?.totals?.totalReviewed} />
                            </div>
                            <div className="w-20 hidden md:block">
                              <MiniBar accuracy={group?.totals?.accuracy || 0} />
                            </div>
                          </div>
                        </button>
                        {/* Individual Books (expanded) */}
                        {isExpanded && (
                          <div className="divide-y divide-border/50">
                            {/* Sub-table header */}
                            <div className="grid grid-cols-12 gap-2 px-6 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              <div className="col-span-4">Book</div>
                              <div className="col-span-2 text-center">Passages</div>
                              <div className="col-span-2 text-center">Verses</div>
                              <div className="col-span-2 text-center">Reviews</div>
                              <div className="col-span-2 text-center">Accuracy</div>
                            </div>
                            {group?.books?.map((book, idx) => {
                              const hasBookData = book?.verses > 0;
                              return (
                                <div
                                  key={idx}
                                  className={`grid grid-cols-12 gap-2 px-6 py-2.5 items-center text-sm transition-colors ${hasBookData ? 'hover:bg-muted/20' : 'opacity-50'}`}
                                >
                                  <div className="col-span-4 font-medium text-foreground flex items-center gap-2">
                                    {hasBookData && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${book?.accuracy >= 80 ? 'bg-success' : book?.accuracy >= 60 ? 'bg-warning' : book?.accuracy !== null ? 'bg-destructive' : 'bg-primary'}`}></span>}
                                    {!hasBookData && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-muted"></span>}
                                    {book?.book}
                                  </div>
                                  <div className="col-span-2 text-center text-muted-foreground">{hasBookData ? book?.passages : '—'}</div>
                                  <div className="col-span-2 text-center text-muted-foreground">{hasBookData ? book?.verses : '—'}</div>
                                  <div className="col-span-2 text-center text-muted-foreground">{hasBookData ? book?.totalReviewed : '—'}</div>
                                  <div className="col-span-2 text-center">
                                    {hasBookData
                                      ? <AccuracyBadge accuracy={book?.accuracy} reviewed={book?.totalReviewed} />
                                      : <span className="text-xs text-muted-foreground/50">not started</span>
                                    }
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Success Rate — Enhanced */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">Overall Success Rate</h3>
              <p className="text-sm text-muted-foreground mb-6">Your review accuracy across all verses</p>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : userSuccessRate !== null && totalReviewsAllTime > 0 ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8">
                  {/* Gauge */}
                  <div className="relative w-32 h-32 flex-shrink-0">
                    <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="50" fill="none" stroke="var(--color-muted)" strokeWidth="12" />
                      <circle cx="60" cy="60" r="50" fill="none" stroke="var(--color-success)" strokeWidth="12"
                        strokeDasharray={`${2 * Math.PI * 50}`}
                        strokeDashoffset={`${2 * Math.PI * 50 * (1 - userSuccessRate)}`}
                        strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-foreground">{Math.round(userSuccessRate * 100)}%</span>
                    </div>
                  </div>

                  {/* Stats breakdown */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <p className="text-foreground font-semibold text-base">
                        {userSuccessRate >= 0.9 ? '🌟 Excellent retention!' : userSuccessRate >= 0.75 ? '✅ Good progress!' : '📈 Keep practicing!'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">Used as baseline for 90-day forecast</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-muted/30 rounded-lg p-3 text-center border border-border/50">
                        <p className="text-xl font-bold text-foreground">{totalReviewsAllTime?.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Total Reviews</p>
                      </div>
                      <div className="bg-success/10 rounded-lg p-3 text-center border border-success/20">
                        <p className="text-xl font-bold text-success">{totalSuccessfulAllTime?.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Successful</p>
                      </div>
                      <div className="bg-destructive/10 rounded-lg p-3 text-center border border-destructive/20">
                        <p className="text-xl font-bold text-destructive">{(totalReviewsAllTime - totalSuccessfulAllTime)?.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Missed</p>
                      </div>
                    </div>

                    {/* Accuracy bar */}
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Accuracy</span>
                        <span>{Math.round(userSuccessRate * 100)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full transition-all ${userSuccessRate >= 0.9 ? 'bg-success' : userSuccessRate >= 0.75 ? 'bg-warning' : 'bg-destructive'}`}
                          style={{ width: `${Math.round(userSuccessRate * 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0%</span>
                        <span className="text-warning">75%</span>
                        <span className="text-success">90%+</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground">Complete some reviews to see your success rate</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'forecast' && (
          <>
            {/* 90-Day Forecast Histogram */}
            <div data-pdf-chart="90-Day Review Forecast" className="bg-card rounded-lg border border-border p-6 mb-8">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <BarChart2 className="w-5 h-5 text-primary" />
                    90-Day Review Forecast
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Projected daily review load based on your{' '}
                    {userSuccessRate !== null && totalReviewsAllTime >= 10
                      ? <span className="text-success font-medium">{Math.round(userSuccessRate * 100)}% actual success rate</span>
                      : <span className="text-warning font-medium">90% default retention rate</span>
                    }
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full">
                  <Zap className="w-3 h-3 text-warning" />
                  Deterministic projection
                </div>
              </div>

              <div className="bg-muted/20 border border-border/50 rounded-lg p-3 mb-4 text-xs text-muted-foreground">
                <strong className="text-foreground">How this works:</strong> Starting from each verse's current <code className="bg-muted px-1 rounded">next_review_date</code> and phase, 
                the forecast simulates spaced repetition advancement over 90 days using your retention rate (90% default if fewer than 10 reviews). 
                Successful reviews advance the phase (longer intervals); missed reviews regress it. No new verses are assumed.
              </div>

              {forecastLoading ? (
                <div className="flex items-center justify-center h-[350px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : forecastData?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={forecastData} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="displayDate" angle={-45} textAnchor="end" height={80}
                        interval={Math.floor(forecastData?.length / 15) || 0}
                        tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }} />
                      <YAxis label={{ value: 'Daily Reviews', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                        tick={{ fill: 'var(--color-muted-foreground)' }} />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload?.length) {
                            const data = payload?.[0]?.payload;
                            return (
                              <div className="bg-card border border-border rounded-lg shadow-lg p-3">
                                <p className="font-semibold text-foreground">{data?.displayDate}</p>
                                <p className="text-sm text-muted-foreground">Day {data?.day + 1} of 90</p>
                                <p className="text-sm font-medium text-primary">{data?.count} projected review{data?.count !== 1 ? 's' : ''}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <ReferenceLine y={Math.round(forecastData?.reduce((sum, d) => sum + d?.count, 0) / forecastData?.length)}
                        stroke="var(--color-warning)" strokeDasharray="4 4"
                        label={{ value: 'Avg', fill: 'var(--color-warning)', fontSize: 11 }} />
                      <Bar dataKey="count" fill="var(--color-primary)" radius={[2, 2, 0, 0]} opacity={0.85} />
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Forecast Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    {[
                      { label: 'Avg Daily Reviews', value: Math.round(forecastData?.reduce((sum, d) => sum + d?.count, 0) / forecastData?.length), color: 'text-primary' },
                      { label: 'Peak Day', value: Math.max(...forecastData?.map(d => d?.count)), color: 'text-warning' },
                      { label: 'Total (90 days)', value: forecastData?.reduce((sum, d) => sum + d?.count, 0), color: 'text-success' },
                      { label: 'Light Days (<5)', value: forecastData?.filter(d => d?.count < 5)?.length, color: 'text-accent' }
                    ]?.map((stat, i) => (
                      <div key={i} className="bg-muted/30 rounded-lg p-4 text-center border border-border/50">
                        <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{stat?.label}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground gap-3">
                  <RefreshCw className="w-12 h-12 text-muted-foreground/40" />
                  <p>No active verses to forecast. Add verses to your library to see projections.</p>
                </div>
              )}
            </div>

            {/* Suggested Metrics Info */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-warning" />
                Available Metrics from Your Data
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Review Success Rate Over Time', desc: 'Track how your accuracy improves week-over-week as verses mature through phases', available: true },
                  { title: 'Phase Graduation Rate', desc: 'How quickly verses advance from Phase 1 to mature phases — shows memorization velocity', available: true },
                  { title: 'Daily Review Load Trend', desc: 'Historical daily review counts from user_daily_activity — visible in Progress tab', available: true },
                  { title: 'Book-Level Accuracy', desc: 'Which Bible books you retain best vs. struggle with — visible in Performance tab', available: true },
                  { title: 'Streak Consistency', desc: 'Calendar heatmap of daily review completion — visible in Overview tab', available: true },
                  { title: 'Verse Age vs. Retention', desc: 'Correlation between how long a verse has been in library and its current phase', available: false }
                ]?.map((metric, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${metric?.available ? 'border-success/30 bg-success/5' : 'border-border bg-muted/20'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${metric?.available ? 'bg-success' : 'bg-muted-foreground'}`}></div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{metric?.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{metric?.desc}</p>
                        {!metric?.available && <p className="text-xs text-warning mt-1">Requires additional data tracking</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'awards' && (
          <AwardsProgressPanel />
        )}
        </FadeSlideIn>
        </div>
      )}
    </div>
  );
};

export default StudyAnalyticsDashboard;